// ----- Phase E-polish-extract-3 — shared ConfirmModal component ----- //
//
// Extracted from inline copies in 6 admin pages (SpecFilesPage,
// PromptLibraryPage, FormTemplatesPage, CompanyProfilePage,
// UsersPage, SettingsPage). ValidationRulesPage is read-only and
// has no ConfirmModal — same 6-page set as the PageToast extraction
// in Phase E-polish-extract-2.
//
// Pre-extraction copies split into two style families. All 6 used
// the same prop shape (`{ title, body, confirmLabel?, destructive?,
// onCancel, onConfirm }`), the same role/aria/event behavior, and
// the same `data-testid` values (`confirm-modal-cancel`,
// `confirm-modal-confirm`). The differences were purely cosmetic:
//
//   - "Compact" (SpecFilesPage, SettingsPage)
//     width 420px / 90vw, title margin-bottom 12px,
//     body fontSize 14, body+cancel color `var(--text)`,
//     destructive `var(--red)`.
//
//   - "Muted" (PromptLibraryPage, FormTemplatesPage,
//     CompanyProfilePage, UsersPage)
//     width 440px / 92vw, title margin-bottom 8px,
//     body fontSize 13, body+cancel color `var(--text-muted)`,
//     destructive `var(--red, #c53030)` (palette fallback).
//
// Reconciliation: the "Muted" palette wins (4-of-6 majority and the
// more recent codebase direction — newer admin pages were authored
// in this style). SpecFiles + Settings get a subtle cosmetic
// harmonization: slightly wider modal, slightly smaller body text in
// muted color. The data-testids, aria attributes, role, and the
// escape-on-backdrop-click behavior are preserved exactly so every
// per-page test continues to pass without modification.
//
// Canonical prop shape (per Phase E-polish-extract-3 prompt):
//
//   { isOpen?, onConfirm, onCancel, title, message,
//     confirmLabel?, cancelLabel?, variant?, isLoading? }
//
// Renames vs the pre-extraction shape:
//   - `body` → `message`
//   - `destructive?: boolean` → `variant?: 'default' | 'destructive'`
//
// New affordances:
//   - `isOpen?: boolean` (default `true`). Most consumers control
//     visibility via `{deleteTarget && <ConfirmModal …/>}` (the
//     existing pattern). New consumers that prefer always-rendered
//     modals can set `isOpen={false}` to render null.
//   - `cancelLabel?: string` (default `"Cancel"`).
//   - `isLoading?: boolean` — disables BOTH buttons and prevents
//     backdrop-click and Escape-key close. Use it across async
//     `onConfirm` operations to prevent double-submit + accidental
//     dismissal mid-mutation.
//   - Escape-key handler — wasn't in the pre-extraction copies.
//     Added as part of the canonical accessible-dialog upgrade.
//   - `aria-labelledby` pointing at the rendered h2 — replaces the
//     pre-extraction `aria-label={title}`. Tests that use
//     `getByRole('dialog', { name: /…/ })` still pass because the
//     accessible name is derived from the h2 text.

import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { MovableDialog } from './MovableDialog';
import { matchesTypeToConfirm } from './typeToConfirm';

export type ConfirmModalVariant = 'default' | 'destructive';

export interface ConfirmModalProps {
  /**
   * Defaults to true. Set to false to render null. Most consumers
   * already control visibility via `{x && <ConfirmModal …/>}` and
   * can omit this; provided for symmetry with always-rendered modal
   * patterns.
   */
  readonly isOpen?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly title: string;
  readonly message: string;
  /** Defaults to "Confirm". */
  readonly confirmLabel?: string;
  /** Defaults to "Cancel". */
  readonly cancelLabel?: string;
  /**
   * Defaults to 'default'. 'destructive' paints the confirm button
   * red — use for delete flows.
   */
  readonly variant?: ConfirmModalVariant;
  /**
   * When true, disables BOTH buttons, blocks backdrop-click close,
   * and blocks the Escape-key handler. Use across async `onConfirm`
   * operations so the user can't dismiss the modal mid-mutation or
   * double-submit.
   */
  readonly isLoading?: boolean;
  /**
   * phase-entity-model-stage-1-delete-safety — disables ONLY the confirm
   * button. `isLoading` disables both and blocks Escape/backdrop, which is
   * right for "a mutation is in flight" but wrong for "we are not ready to
   * let you confirm yet" (e.g. still checking what a delete would affect):
   * a person must always be able to back out.
   */
  readonly confirmDisabled?: boolean;
  /**
   * T-06 row 13 / D-17 — OPT-IN, default 'default'.
   *
   * 'danger' tones the TITLE red as well as the confirm button, so a
   * permanent removal cannot be mistaken at a glance for a reversible one.
   * Deliberately not implied by `variant: 'destructive'`: several pages
   * already pass that for reversible-enough deletes, and turning their titles
   * red would change screens that belong to other test surfaces and have not
   * been verified for it. They can opt in when they are.
   */
  readonly titleTone?: 'default' | 'danger';
  /**
   * T-06 row 13 / D-17 — OPT-IN, default absent (no gate).
   *
   * Requires the person to type `expected` before Confirm becomes usable.
   * Reserve it for actions that cannot be undone: it costs real effort, and
   * spending that on a reversible action trains people to type past it.
   */
  readonly typeToConfirm?: {
    /** What must be typed — for a user removal, their email address. */
    readonly expected: string;
    /** The visible label above the box. Say what to type, not "are you sure". */
    readonly label: string;
  };
  /**
   * Form Templates row 8 — optional supporting content under the message
   * (e.g. the diff of what a restore replaces). Widens the dialog to fit.
   */
  readonly children?: ReactNode;
  /**
   * D-29 — omit the confirm button entirely. Opt-in, default false, so no
   * existing consumer changes. For a dialog that reports there is nothing
   * to do: `confirmDisabled` leaves a greyed button on screen inviting a
   * click that would mean nothing, which is not the same as having no
   * action to offer.
   *
   * Independent of `typeToConfirm` above: that gates a confirm the person
   * must still make, this one says there is no confirm to make. Setting both
   * is meaningless but harmless — hiding wins, since a hidden button cannot
   * be gated.
   */
  readonly hideConfirm?: boolean;

}

const TITLE_ID = 'shared-confirm-modal-title';

export function ConfirmModal({
  isOpen = true,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
  confirmDisabled = false,
  titleTone = 'default',
  typeToConfirm,
  children,
  hideConfirm = false,

}: ConfirmModalProps): ReactElement | null {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const [typed, setTyped] = useState('');
  const typedFieldId = useId();

  // Escape key closes the modal (unless busy). Kept as the global listener
  // (not only the MovableDialog focus-trap handler) so Escape works even
  // when focus sits outside the dialog — the pre-conversion behaviour some
  // page tests rely on.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape' && !isLoading) onCancel();
    };
    globalThis.addEventListener('keydown', handler);
    return () => globalThis.removeEventListener('keydown', handler);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  const destructive = variant === 'destructive';
  const danger = titleTone === 'danger';
  // The gate is unmet until the expected value is typed; absent prop = no gate.
  const typedGateUnmet = typeToConfirm !== undefined
    && !matchesTypeToConfirm(typed, typeToConfirm.expected);
  const confirmBlocked = isLoading || confirmDisabled || typedGateUnmet;

  // phase-movable-dialogs — confirms render through the ONE movable shell:
  // draggable by the title bar, clamped on-screen, remembered position
  // (single key for all confirms — they are one dialog kind), backdrop
  // click still cancels (unless busy), exactly as before.
  return (
    <MovableDialog
      persistKey="shared.confirm"
      title={
        <span
          id={TITLE_ID}
          style={{
            fontSize: 16,
            ...(danger ? { color: 'var(--red, #c53030)', fontWeight: 700 } : {}),
          }}
        >
          {title}
        </span>
      }
      ariaLabelledBy={TITLE_ID}
      onClose={isLoading ? undefined : onCancel}
      escDisabled={isLoading}
      closeOnBackdrop={!isLoading}
      width={children ? 'min(760px, 94vw)' : 'min(440px, 92vw)'}
      zIndex={350}
    >
      <>
        <p style={{ margin: '0 0 20px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          {message}
        </p>
        {children && <div style={{ margin: '0 0 20px 0' }}>{children}</div>}
        {typeToConfirm && (
          <div style={{ margin: '0 0 20px 0' }}>
            <label
              htmlFor={typedFieldId}
              style={{ display: 'block', fontSize: 13, marginBottom: 6, color: 'var(--text)' }}
            >
              {typeToConfirm.label}
            </label>
            <input
              id={typedFieldId}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
              data-testid="confirm-modal-type-to-confirm"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                fontSize: 13,
                borderRadius: 6,
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                color: 'var(--text)',
              }}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            ref={cancelButtonRef}
            onClick={onCancel}
            disabled={isLoading}
            autoFocus
            data-testid="confirm-modal-cancel"
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          {!hideConfirm && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmBlocked}
            data-testid="confirm-modal-confirm"
            style={{
              background: destructive ? 'var(--red, #c53030)' : 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: confirmBlocked ? 'not-allowed' : 'pointer',
              opacity: confirmBlocked ? 0.6 : 1,
            }}
          >
            {confirmLabel}
          </button>
          )}
        </div>
      </>
    </MovableDialog>
  );
}
